<template>
	<!-- eslint-disable max-lines-->
	<div class="request-payload-mapping">
		<QuestionGroup>
			<QuestionCard
				title="What data should be sent in the request payload?"
				description="Build the payload to be used for sending data. Use Classic Templating to create a JSON template with [PLACEHOLDER_FIELDNAME] format, or use JavaScript Data Transformations to write custom mapping logic for complex data manipulation."
			>
				<!-- Mapping Type Selector Cards -->
				<div class="mapping-type-selector">
					<SelectorCard
						title="Classic Templating"
						description="JSON template with placeholders"
						:model-value="selectedMappingType === 'classic'"
						:disabled="!userFullPermissions"
						@input="selectMappingType('classic')"
					>
						<template #icon>
							<v-icon
								color="primary"
								size="24"
							>
								mdi-code-braces
							</v-icon>
						</template>
					</SelectorCard>
					<SelectorCard
						title="JavaScript Data Transformations"
						description="Custom JavaScript mapping logic"
						:model-value="selectedMappingType === 'javascript'"
						:disabled="!userFullPermissions"
						@input="selectMappingType('javascript')"
					>
						<template #icon>
							<v-icon
								color="primary"
								size="24"
							>
								mdi-language-javascript
							</v-icon>
						</template>
						<template #title>
							JavaScript Data Transformations
							<v-chip
								x-small
								color="primary"
								class="ml-2"
								label
							>
								beta
							</v-chip>
						</template>
					</SelectorCard>
				</div>

				<!-- Classic JSON Editor Section with Integrated Chip Container -->
				<div
					v-if="selectedMappingType === 'classic'"
					class="json-editor-with-chips"
				>
					<!-- Selected Fields Chips - Integrated Header -->
					<div
						v-if="selectedFieldObjects.length > 0"
						class="json-editor-chips-header"
					>
						<div class="chips-header-content">
							<div class="selected-fields-chips">
								<DraggableFieldChip
									v-for="(field, index) in selectedFieldObjects"
									:key="`field-${index}-${field.fieldName || 'unknown'}`"
									:field="field"
									:usage-info="fieldUsageMap[field.fieldName]"
									class="mr-2"
									@dragstart="handleChipDragStart"
									@dragend="handleChipDragEnd"
								/>
							</div>
						</div>
					</div>

					<!-- JSON Editor Container -->
					<div class="json-editor-body connector-service-json-editor">
						<JsonEditor
							:value="requestBody"
							:disabled="!userFullPermissions"
							:drag-field="currentDragField"
							:available-fields="selectedFieldObjects"
							show-token-chips
							class="json-editor-container"
							@input="handleRequestBodyInput"
							@drop="handleJsonEditorDrop"
						/>

						<!-- JSON Validation Error Display -->
						<v-alert
							v-if="jsonValidationError"
							type="warning"
							border="bottom"
							colored-border
							dense
							outlined
							color="warning"
							class="mt-2 json-validation-error"
						>
							<pre class="json-validation-error__pre">{{ jsonValidationError }}</pre>
						</v-alert>
					</div>
				</div>

				<!-- JavaScript Object Mapping Script Editor -->
				<div
					v-if="selectedMappingType === 'javascript'"
					class="script-editor-wrapper"
				>
					<ScriptEditor
						:value="requestBody"
						:disabled="!userFullPermissions"
						:available-fields="selectedFieldObjects"
						:enable-jint-validation="true"
						language="javascript"
						theme="vs"
						:placeholder="jsStarterCode"
						class="script-editor-container"
						@input="handleRequestBodyInput"
						@validation-change="handleJintValidationChange"
					/>

					<!-- Jint Validation Feedback -->
					<div
						v-if="jintValidation.hasErrors || jintValidation.hasWarnings"
						class="jint-validation-summary mt-3"
					>
						<!-- Blocked Patterns (Errors) -->
						<v-alert
							v-if="jintValidation.blockedPatterns.length > 0"
							type="error"
							dense
							outlined
							class="mb-2"
						>
							<div class="font-weight-bold mb-1">
								<v-icon
									small
									left
								>
									mdi-alert-circle
								</v-icon>
								Blocked Patterns Detected
							</div>
							<div class="caption mb-2">
								The following patterns are not supported and will cause runtime errors:
							</div>
							<ul class="jint-validation-list">
								<li
									v-for="pattern in jintValidation.blockedPatterns"
									:key="pattern"
								>
									<code>
										{{ pattern }}
									</code>
								</li>
							</ul>
						</v-alert>

						<!-- ES6+ Features (Warnings) -->
						<v-alert
							v-if="jintValidation.es6Features.length > 0"
							type="warning"
							dense
							outlined
							class="mb-2"
						>
							<div class="font-weight-bold mb-1">
								<v-icon
									small
									left
								>
									mdi-alert
								</v-icon>
								ES6+ Features Detected
							</div>
							<div class="caption mb-2">
								These features may not work (ES5.1 only). See inline suggestions in the editor.
							</div>
							<ul class="jint-validation-list">
								<li
									v-for="feature in jintValidation.es6Features"
									:key="feature"
								>
									<code>
										{{ feature }}
									</code>
								</li>
							</ul>
						</v-alert>

						<!-- Suspicious Patterns (Info) -->
						<v-alert
							v-if="jintValidation.suspiciousPatterns.length > 0"
							type="info"
							dense
							outlined
							class="mb-2"
						>
							<div class="font-weight-bold mb-1">
								<v-icon
									small
									left
								>
									mdi-information
								</v-icon>
								Review Required
							</div>
							<div class="caption mb-2">
								These patterns should be reviewed to ensure they are intentional:
							</div>
							<ul class="jint-validation-list">
								<li
									v-for="pattern in jintValidation.suspiciousPatterns"
									:key="pattern"
								>
									<code>
										{{ pattern }}
									</code>
								</li>
							</ul>
						</v-alert>
					</div>
				</div>
			</QuestionCard>
		</QuestionGroup>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import DOMPurify from 'dompurify'
import { JsonEditor, ScriptEditor, QuestionGroup, QuestionCard, SelectorCard } from '../../../../../components/component-library-wrapper.js'
import { isLenientJsonValid } from '../utils/lenient-json.js'
import { formatJsonError } from '../../../../../../shared/utils/json-error-formatting.js'
import DraggableFieldChip from './DraggableFieldChip.vue'

export default {
	name: 'request-payload-mapping',
	components: {
		JsonEditor,
		ScriptEditor,
		QuestionGroup,
		QuestionCard,
		SelectorCard,
		DraggableFieldChip
	},
	props: {
		requestBody: String,
		userFullPermissions: Boolean,
		isEdit: {
			type: Boolean,
			default: false
		},
		cloneMode: {
			type: Boolean,
			default: false
		},
		selectedFields: {
			type: Array,
			default: () => []
		},
		fieldUsageMap: {
			type: Object,
			default: () => ({})
		},
		availableFields: {
			type: Array,
			default: () => []
		}
	},
	data () {
		return {
			selectedMappingType: this.detectMappingType(),
			currentDragField: null,
			hasGeneratedSample: false,
			jsonValidationError: null,
			jsStarterCode: `// Enter your JavaScript mapping code here...\n// Return the object to be sent in the request payload\n\nreturn {\n  // your mapping logic\n}`,
			jsonEditorPlaceholder: `{
  "contact": {
    "firstName": "[PLACEHOLDER_FIRST_NAME]",
    "lastName": "[PLACEHOLDER_LAST_NAME]",
    "email": "[PLACEHOLDER_EMAIL_ADDRESS]"
  }
}`,
			// Local caches so users (create, clone, and edit) can switch between mapping modes without losing work
			cachedClassicBody: null,
			cachedJavascriptBody: null,
			// Jint validation state
			jintValidation: {
				hasErrors: false,
				hasWarnings: false,
				blockedPatterns: [],
				es6Features: [],
				suspiciousPatterns: []
			}
		}
	},
	computed: {
		selectedFieldObjects () {
			// Convert selected field names to field objects
			return this.selectedFields.map(fieldName => {
				const field = this.availableFields.find(f => f.fieldName === fieldName)
				return field || { ...fieldName }
			}).filter(Boolean)
		}
	},

	watch: {
		selectedFieldObjects: {
			handler (newFields) {
				// Only generate sample if we haven't already and have no requestBody
				if (!this.requestBody && newFields.length > 0 && !this.hasGeneratedSample) {
					const sampleJson = this.buildSampleJsonFromFields()
					this.hasGeneratedSample = true
					this.$emit('update:requestBody', sampleJson)
				}
			},
			immediate: true
		},

		requestBody: {
			handler (newValue, oldValue) {
				// Reset the flag if requestBody is manually cleared
				if (!newValue) {
					this.hasGeneratedSample = false
				}
				// Re-validate when requestBody changes (including when navigating back to this step)
				this.validateJson(newValue)

				// Auto-detect mapping type ONLY on initial load
				// Don't switch modes during user editing
				// Only detect if:
				// 1. There's new content
				// 2. Old value was empty/undefined (initial load scenario)
				// 3. New content is substantial (more than 10 characters to avoid single char edits)
				if (newValue &&
					(!oldValue || oldValue.trim() === '') &&
					newValue.trim().length > 10) {
					this.selectedMappingType = this.detectMappingType()
					// Emit the detected mapping type to parent
					this.$emit('update:mappingType', this.selectedMappingType)
				}
			},
			immediate: true
		}
	},
	mounted () {
		// Detect mapping type on mount (for edit mode)
		this.selectedMappingType = this.detectMappingType()
		// Emit the detected mapping type to parent
		this.$emit('update:mappingType', this.selectedMappingType)
		// Validate JSON when component mounts (e.g., when navigating back to this step)
		this.validateJson(this.requestBody)

		// For cloned systems or new integrations with JavaScript mode and empty requestBody,
		// the ScriptEditor placeholder won't show because the value is set to empty string.
		// We don't want to auto-populate placeholder values that could be saved accidentally,
		// but we can initialize with a comment to guide users.
		if ((!this.isEdit || this.cloneMode) &&
				this.selectedMappingType === 'javascript' &&
				(!this.requestBody || this.requestBody.trim() === '')) {
			// Initialize with starter comment for JavaScript transformations
			// This provides guidance without persisting placeholder data
			this.$emit('update:requestBody', this.jsStarterCode)
		}
	},
	methods: {
		detectMappingType () {
			// If no requestBody, default to classic
			if (!this.requestBody || this.requestBody.trim() === '') {
				return 'classic'
			}

			const content = this.requestBody.trim()

			// Check for JavaScript transformation indicators
			const jsIndicators = [
				/\bfunction\s+\w+/, // function keyword with name
				/\breturn\s+/, // return statement
				/=>/, // arrow functions
				/\b(const|let|var)\s+\w+/, // variable declarations
				/\bif\s*\(/, // if statements
				/\bfor\s*\(/, // for loops
				/\bwhile\s*\(/, // while loops
				/\/\//, // JavaScript comments
				/\/\*/, // JavaScript block comments
				/\bdata\./, // data object access
				/\bsystem\./, // system object access
				/\bencode\./, // encode object access
				/\bdecode\./, // decode object access
				/\bconfig\./ // config object access
			]

			// If any JavaScript indicator is found, it's JavaScript mode
			const isJavaScript = jsIndicators.some(regex => regex.test(content))

			return isJavaScript ? 'javascript' : 'classic'
		},

		selectMappingType (type) {
			if (type === this.selectedMappingType) return

			// Cache current content and restore the other mode's cached content (same for create, edit and clone)
			if (type === 'javascript') {
				this.cachedClassicBody = this.requestBody
				this.$emit('update:requestBody', this.cachedJavascriptBody || this.jsStarterCode)
			} else if (type === 'classic') {
				this.cachedJavascriptBody = this.requestBody
				const fallback = this.buildSampleJsonFromFields() || ''
				this.$emit('update:requestBody', this.cachedClassicBody !== null ? this.cachedClassicBody : fallback)
			}

			this.selectedMappingType = type
			this.$emit('update:mappingType', type)
		},

		handleRequestBodyInput (value) {
			// Sanitize input for JavaScript transformations to prevent XSS attacks
			let sanitizedValue = value
			if (this.selectedMappingType === 'javascript') {
				sanitizedValue = this.sanitizeJavaScriptInput(value)
			}

			// Validate JSON on input
			this.validateJson(sanitizedValue)
			// Emit the sanitized value to parent component
			this.$emit('update:requestBody', sanitizedValue)
		},

		sanitizeJavaScriptInput (input) {
			// Ensure input is a string
			if (input === null || input === undefined) {
				return ''
			}
			const stringInput = String(input)

			// Length limit to prevent DoS on the server
			const MAX_LENGTH = 100000 // 100KB limit
			if (stringInput.length > MAX_LENGTH) {
				console.warn('JavaScript transformation exceeds maximum length')
				return stringInput.substring(0, MAX_LENGTH)
			}

			// Guard against missing DOMPurify
			if (!DOMPurify) {
				console.warn('DOMPurify is not available')
				return ''
			}

			// Protect existing HTML entities by escaping ampersands
			// This prevents &lt; from being converted to < during the decode step
			// We only want to decode entities that DOMPurify created (like < becoming &lt;)
			const protectedInput = stringInput.replace(/&/g, '&amp;')

			// DOMPurify sanitizes potentially harmful HTML/script content
			// Configure to allow text content but strip dangerous tags and attributes
			const sanitized = DOMPurify.sanitize(protectedInput, {
				ALLOWED_TAGS: [], // Strip all HTML tags, keep only text content
				ALLOWED_ATTR: [], // Strip all attributes
				KEEP_CONTENT: true // Keep the text content of removed tags
			})

			// Decode HTML entities back to characters (e.g. &lt; to <)
			// This is required because DOMPurify escapes characters like '<' which are valid JavaScript operators.
			// We decode them back to ensure the code syntax remains valid while having stripped actual HTML tags.
			try {
				const div = document.createElement('div')
				div.innerHTML = sanitized
				return div.textContent || ''
			} catch (error) {
				console.error('Error sanitizing JavaScript input:', error)
				return ''
			}
		},

		// Preprocess a JSON-like string to strict JSON by quoting unquoted bareword values
		// Example: { "key": value } -> valid by quoting value under the hood

		validateJson (jsonString) {
			// Clear previous validation error
			this.jsonValidationError = null

			// Skip validation for empty values
			if (!jsonString || jsonString.trim() === '') {
				return
			}

			// Skip JSON validation for JavaScript transformations
			if (this.selectedMappingType === 'javascript') {
				// JavaScript transformations are validated by ScriptEditor's Jint validator
				// No need for JSON validation here
				return
			}

			// Only validate JSON for classic templating mode
			// Validate leniently: accept unquoted values
			if (!isLenientJsonValid(jsonString)) {
				// If it fails even after leniency, try strict parse to show detailed error message
				try {
					JSON.parse(jsonString)
				} catch (error) {
					this.jsonValidationError = formatJsonError(error, jsonString)
				}
			}
		},

		// Removed local formatting helpers in favor of shared utility (formatJsonError)
		buildSampleJsonFromFields () {
			if (this.selectedFieldObjects.length === 0) {
				return this.jsonEditorPlaceholder
			}

			const sampleObject = {}
			this.selectedFieldObjects.forEach(field => {
				const fieldName = field.fieldName || field.name
				if (fieldName) {
					// Create placeholder format for each field
					sampleObject[fieldName] = `[PLACEHOLDER_${fieldName.toUpperCase()}]`
				}
			})

			return JSON.stringify(sampleObject, null, 2)
		},

		handleJsonEditorDrop (field, dropInfo) {
			// Emit the drop event to the parent component
			this.$emit('field-drop', {
				field: field,
				context: 'payload',
				position: dropInfo.position
			})
		},

		handleChipDragStart (field) {
			// Set the current drag field when a chip starts being dragged
			this.currentDragField = field
		},

		handleChipDragEnd () {
			// Clear the current drag field when dragging ends
			this.currentDragField = null
		},

		/**
		 * Handle validation feedback from ScriptEditor's Jint validator
		 */
		handleJintValidationChange (validationResult) {
			this.jintValidation = {
				hasErrors: validationResult.hasErrors,
				hasWarnings: validationResult.hasWarnings,
				blockedPatterns: validationResult.blockedPatterns || [],
				es6Features: validationResult.es6Features || [],
				suspiciousPatterns: validationResult.suspiciousPatterns || []
			}

			// Emit validation status to parent for save button state
			this.$emit('validation-status', {
				canSave: !validationResult.hasErrors
			})
		}
	}
}
</script>

<style lang="scss">
.request-payload-mapping {
	display: flex;
	flex-direction: column;
	width: 100%;
}

.json-editor-with-chips {
	background: transparent;
}

.json-editor-chips-header {
	background: transparent;
	border-bottom: none;
	padding: 0 0 12px 0;
}

.chips-header-content {
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
}

.chips-icon {
	color: var(--global-muted-foreground);
}

.chips-label {
	font-size: 13px;
	font-weight: 500;
	color: #475569;
	white-space: nowrap;
}

.selected-fields-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	flex: 1;
}

.json-editor-body {
	position: relative;
}

.connector-service-json-editor .CodeMirror .CodeMirror-scroll {
	min-height: 400px !important;
}

.json-validation-error {
	width: fit-content;
	background-color: transparent !important;
	border: 1px solid #fb8c00 !important;
}

.json-validation-error__pre {
	margin: 0;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	font-size: 12px;
	line-height: 1.4;
	white-space: pre-wrap;
	max-width: 800px;
	overflow-x: auto;
}

.json-editor-container {
	width: 100%;
	min-height: 400px;
	border: none;
	border-radius: 0;

	:deep(.json-editor__container) {
		border: none;
		border-radius: 0;
		background: #ffffff;
	}

	:deep(.CodeMirror) {
		border: none;
		border-radius: 0;
		font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
	}
}

.mapping-type-selector {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-gap: 16px;
	margin-bottom: 24px;
	align-items: stretch;
}

.script-editor-wrapper {
	background: transparent;
	margin-top: 12px;
}

.script-editor-container {
	width: 100%;
	min-height: 400px;
}

/* Jint Validation Summary Styles */
.jint-validation-summary {
	max-width: 100%;
}

.jint-validation-list {
	margin: 0;
	padding-left: 20px;
	list-style-type: disc;

	li {
		margin-bottom: 4px;

		&:last-child {
			margin-bottom: 0;
		}

		code {
			background-color: rgba(0, 0, 0, 0.06);
			padding: 2px 6px;
			border-radius: var(--radius-xs, 2px);
			font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
			font-size: 12px;
		}
	}
}

.jint-validation-summary .v-alert {
	max-width: fit-content;
}

.jint-validation-summary .v-alert--error code {
	background-color: rgba(244, 67, 54, 0.1);
	color: #c62828;
}

.jint-validation-summary .v-alert--warning code {
	background-color: rgba(255, 152, 0, 0.1);
	color: #e65100;
}

.jint-validation-summary .v-alert--info code {
	background-color: rgba(33, 150, 243, 0.1);
	color: #1565c0;
}
</style>
